/**
 * Calls the `seo-api` edge function. It is used for anything paid or privileged
 * (DataForSEO, Claude, Google, admin). Reads go straight to the tables through `supabase`.
 */
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL, supabase } from './supabase';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export const API_BASE = `${SUPABASE_URL}/functions/v1/seo-api`;

/**
 * `callApi<T>('/research/keywords', {seed})` → POST with JSON body. With no body it is a GET,
 * unless `init.method` says otherwise. Sends the user's access token when signed in.
 * Throws `ApiError{status, code, message}` for network failures (status 0), non-2xx responses
 * (`{error:{code,message}}` bodies are unwrapped) and unreadable JSON.
 */
export async function callApi<T>(
  path: string,
  body?: unknown,
  init: { method?: 'GET' | 'POST'; signal?: AbortSignal } = {},
): Promise<T> {
  const method = init.method ?? (body === undefined ? 'GET' : 'POST');
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers: Record<string, string> = { apikey: SUPABASE_PUBLISHABLE_KEY, accept: 'application/json' };
  if (token) headers.authorization = `Bearer ${token}`;
  if (method === 'POST') headers['content-type'] = 'application/json';

  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: method === 'POST' ? JSON.stringify(body ?? {}) : undefined,
      signal: init.signal,
    });
  } catch (e) {
    throw new ApiError(0, 'network', `Could not reach the server (${(e as Error).message || 'network error'}).`);
  }

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      if (res.ok) throw new ApiError(res.status, 'bad_json', 'The server sent a reply that is not JSON.');
    }
  }
  if (!res.ok) {
    const err = (parsed as { error?: { code?: string; message?: string } } | undefined)?.error;
    throw new ApiError(res.status, err?.code ?? `http_${res.status}`, err?.message ?? (text.slice(0, 200) || res.statusText || 'Request failed'));
  }
  return parsed as T;
}

/** GET a text reply (the Markdown pack). Errors are unwrapped like `callApi`. */
export async function callApiText(path: string): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers: Record<string, string> = { apikey: SUPABASE_PUBLISHABLE_KEY, accept: 'text/markdown, application/json' };
  if (token) headers.authorization = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path.startsWith('/') ? path : `/${path}`}`, { headers });
  } catch (e) {
    throw new ApiError(0, 'network', `Could not reach the server (${(e as Error).message || 'network error'}).`);
  }
  const text = await res.text();
  if (!res.ok) {
    let err: { code?: string; message?: string } | undefined;
    try {
      err = (JSON.parse(text) as { error?: { code?: string; message?: string } }).error;
    } catch {
      err = undefined;
    }
    throw new ApiError(res.status, err?.code ?? `http_${res.status}`, err?.message ?? (text.slice(0, 200) || res.statusText || 'Request failed'));
  }
  return text;
}
